import { useDefaultParameterVisit } from "./useDefaultParameterVisit";
import { MaterialDefaultsEditorLayout } from "./MaterialDefaultsEditorLayout";
import { RevertDefaultsButton } from "./RevertDefaultsButton";
import {
  chemistryMeasurementKey,
  chemistryMeasurementsEqual,
  chemistryMatchesDefaults,
  mergeChemistryMeasurements,
} from "components/materialChemistry/chemistryMeasurements";
import { ReviewFixedMixesButton } from "./ReviewFixedMixesButton";
import { useChemistryRowWarnings } from "./useChemistryRowWarnings";
import { restoreChemistryMeasurements } from "./restoreChemistryMeasurements";
import { useCallback, useEffect, useMemo } from "react";
import { Stack } from "@mui/material";
import { useAppDispatch } from "store/store";
import { backendApi } from "store/api/enhancedApi";
import { useNotifyStatus } from "contexts/status";
import { useTenantData } from "contexts/tenant";
import {
  createMapping,
  createNestedMapping,
  isFetchBaseQueryError,
} from "helpers";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { MaterialOverridesTable } from "components/materialChemistry/MaterialOverridesTable";
import { useMaterialChemistryEdits } from "components/materialChemistry/useMaterialChemistryEdits";
import {
  ChemicalElement,
  MaterialRead,
  MaterialChemistrySetRead,
  useGetMaterialChemistrySetQuery,
  useUpdateDefaultMaterialChemistryMutation,
} from "store/api/generatedApi";
import { useDefaultParametersContext } from "./context";
import { useMaterialPropertiesFeasibility } from "./useMaterialPropertiesFeasibility";
import { useMaterialChemistryDraft } from "./useMaterialChemistryDraft";

export const MaterialChemistryDefaultsEditor = ({
  isActive,
  setConfirmOpen,
  defaultSet,
  materials,
  chemicalElements,
  refresh,
}: {
  isActive: boolean;
  setConfirmOpen: (open: boolean) => void;
  defaultSet: MaterialChemistrySetRead;
  materials: MaterialRead[];
  chemicalElements: ChemicalElement[];
  refresh: () => Promise<unknown>;
}) => {
  const tenantName = useTenant();
  useDefaultParameterVisit({
    tenantName,
    page: "chemistry",
    isActive,
  });
  const scrapchefDefaults = useGetMaterialChemistrySetQuery({
    tenantName,
    materialChemistrySetId: "default",
    source: "scrapchef",
  });
  const dispatch = useAppDispatch();
  const { t } = useTenantTranslation();
  const notifyStatus = useNotifyStatus();
  const { impurity_plot_type } = useTenantData();
  const { setTabActions } = useDefaultParametersContext();
  const { source, overrides, setOverrides, discard, isStale, save } =
    useMaterialChemistryDraft(defaultSet);
  const [updateDefaults, { isLoading: isSaving }] =
    useUpdateDefaultMaterialChemistryMutation();
  const { handleUpdateItem, handleDeleteItem, handleAddNewItem } =
    useMaterialChemistryEdits(setOverrides);
  const materialMapping = useMemo(() => {
    const visible = materials.filter(
      (material) => material.visibility === "visible"
    );
    return {
      byIndex: visible,
      byId: createMapping(visible, (material) => material.id),
      byName: createMapping(visible, (material) => material.name),
    };
  }, [materials]);
  const elementMapping = useMemo(
    () => ({
      byIndex: chemicalElements,
      byId: createMapping(chemicalElements, (element) => element.id),
      byName: createMapping(chemicalElements, (element) => element.name),
    }),
    [chemicalElements]
  );
  const materialChemistry = useMemo(
    () =>
      createNestedMapping(
        source.items,
        (measurement) => measurement.material_id,
        (measurement) => measurement.chemical_element_id
      ),
    [source]
  );
  const scrapchefChemistry = useMemo(
    () =>
      scrapchefDefaults.currentData
        ? createNestedMapping(
            scrapchefDefaults.currentData.items,
            (measurement) => measurement.material_id,
            (measurement) => measurement.chemical_element_id
          )
        : undefined,
    [scrapchefDefaults.currentData]
  );
  const restorableMeasurements = useMemo(
    () =>
      (scrapchefDefaults.currentData?.items ?? []).filter(
        (measurement) =>
          materialMapping.byId[measurement.material_id] &&
          elementMapping.byId[measurement.chemical_element_id]
      ),
    [scrapchefDefaults.currentData, materialMapping, elementMapping]
  );
  const effectiveMeasurements = useMemo(
    () => mergeChemistryMeasurements({ measurements: source.items, overrides }),
    [source.items, overrides]
  );
  const canRevert = restorableMeasurements.some(
    (measurement) =>
      !chemistryMeasurementsEqual(
        effectiveMeasurements.get(chemistryMeasurementKey(measurement)),
        measurement
      )
  );
  const matchesScrapchefDefaults =
    scrapchefDefaults.currentData === undefined
      ? null
      : chemistryMatchesDefaults({
          measurements: mergeChemistryMeasurements({
            measurements: defaultSet.items,
            overrides: [],
          }),
          defaults: scrapchefDefaults.currentData.items,
        });
  const applyScrapchefDefaults = () => {
    setOverrides((current) =>
      restoreChemistryMeasurements({
        overrides: current,
        defaults: restorableMeasurements,
        materialIds: new Set(
          materialMapping.byIndex.map((material) => material.id)
        ),
        elementIds: new Set(chemicalElements.map((element) => element.id)),
      })
    );
  };
  const candidate = useMemo(
    () => ({
      material_physics_overrides: null,
      material_chemistry_overrides: overrides,
    }),
    [overrides]
  );
  const {
    request,
    conflicts,
    isChecking,
    hasError,
    chefGroupNameById,
    limitLabelBySignature,
  } = useMaterialPropertiesFeasibility({ isStale, candidate });
  const rowWarnings = useChemistryRowWarnings({
    conflicts,
    chefGroupNameById,
    limitLabelBySignature,
  });
  const hasChanges = overrides.length > 0;

  const onSave = useCallback(
    async (afterSave?: () => void) => {
      if (!hasChanges || isSaving || request === null) return;
      try {
        await save(async (request) => {
          const savedSet = await updateDefaults({
            tenantName,
            updateDefaultMaterialChemistryRequest: request,
          }).unwrap();
          await dispatch(
            backendApi.util.upsertQueryData(
              "getMaterialChemistrySet",
              { tenantName, materialChemistrySetId: "default" },
              savedSet
            )
          );
        });
        notifyStatus({ text: t("defaultsSaved"), type: "success" });
        afterSave?.();
      } catch (error) {
        const stale = isFetchBaseQueryError(error) && error.status === 409;
        notifyStatus({
          text: t(stale ? "defaultsOutOfDate" : "errorSavingChanges"),
          type: "error",
        });
        if (stale) await refresh();
      } finally {
        setConfirmOpen(false);
      }
    },
    [
      dispatch,
      hasChanges,
      isSaving,
      request,
      updateDefaults,
      tenantName,
      save,
      refresh,
      notifyStatus,
      t,
      setConfirmOpen,
    ]
  );
  useEffect(() => {
    if (!isActive) return;
    setTabActions({
      hasChanges,
      isSaving,
      isChecking,
      conflicts,
      onSave,
      onDiscard: discard,
      saveAvailability: request === null ? "blocked" : "ready",
    });
    return () => {
      setTabActions(null);
    };
  }, [
    isActive,
    setTabActions,
    hasChanges,
    isSaving,
    isChecking,
    conflicts,
    onSave,
    discard,
    request,
  ]);

  return (
    <MaterialDefaultsEditorLayout
      description={t("defaultChemistryDescription")}
      matchesScrapchefDefaults={matchesScrapchefDefaults}
      revertButton={
        <RevertDefaultsButton
          disabled={!canRevert || isSaving || isStale}
          warning={t("revertChemistryWarning")}
          onRevert={applyScrapchefDefaults}
        />
      }
      unavailableWarning={
        scrapchefDefaults.isError ? t("scrapchefChemistryUnavailable") : null
      }
      staleWarning={isStale ? t("chemistryDefaultsChanged") : null}
      onDiscard={discard}
      conflictWarning={{
        hasError,
        conflicts,
        isChecking,
        chefGroupNameById,
        limitLabelBySignature,
      }}
    >
      <ReviewFixedMixesButton
        gradeNames={conflicts
          .filter((conflict) => conflict.kind === "fixed_mix_chemistry_breach")
          .flatMap((conflict) => conflict.steel_grade_names)}
        hasChanges={hasChanges}
        isSaving={isSaving}
        isChecking={isChecking}
        disabled={isStale || hasError || request === null}
        conflicts={conflicts}
        onSave={onSave}
      />
      <Stack sx={{ flex: 1, minHeight: 320 }}>
        <MaterialOverridesTable
          materials={materialMapping}
          chemicalElements={elementMapping}
          materialChemistry={materialChemistry}
          materialChemistryOverrides={overrides}
          showScale={impurity_plot_type === "box"}
          onUpdate={handleUpdateItem}
          onRevert={handleDeleteItem}
          rowWarnings={
            isChecking || hasError || request === null ? undefined : rowWarnings
          }
          scrapchefComparison={{
            measurements: scrapchefChemistry,
            onRevert: (measurement) => {
              handleAddNewItem(
                measurement.material_id,
                measurement.chemical_element_id,
                measurement.loc,
                measurement.scale
              );
            },
          }}
        />
      </Stack>
    </MaterialDefaultsEditorLayout>
  );
};
